// src/pages/Automations/constants/triggers.js
// Client outreach automation triggers - 20 specific automations across 6 categories
// Updated: Jan 2026 - Merged ANOC+Annual Review, OEP Jan 7→Jan 2, Re-engagement simplified

import {
  UserPlus,
  Gift,
  Calendar,
  RefreshCw,
  Heart,
  MessageCircle,
  Mail,
  MessageSquare
} from 'lucide-react';

export const AUTOMATION_CATEGORIES = [
  {
    key: 'welcome',
    name: 'New Client Welcome',
    icon: UserPlus,
    description: 'Onboard new clients with a warm welcome sequence',
    color: 'blue',
    triggers: [
      {
        key: 'welcome_day_1',
        name: 'Day 1 Welcome',
        explanation: 'Sends a welcome message the day after enrollment',
        channels: ['sms', 'email'],
        defaultEnabled: true,
        defaultTemplates: {
          sms: {
            content: `Hi {ClientFirstName}! This is {AgentName}, your insurance agent. I wanted to reach out and thank you for your trust in me. Your {CarrierName} enrollment is being processed and your new card should arrive within 2-3 weeks. Feel free to text or call me anytime at {AgentPhone} if you have questions!`
          },
          email: {
            subject: 'Welcome to Your New {CarrierName} Plan!',
            content: `Hi {ClientFirstName},

I wanted to personally reach out and thank you for choosing to work with me on your {CarrierName} coverage. Your trust means a lot, and I'm here to support you every step of the way.

Here's what happens next:
• Your enrollment is currently being processed
• Your new {CarrierName} ID card should arrive in 2-3 weeks
• Keep your current card handy until the new one arrives

In the meantime, please don't hesitate to reach out if you have any questions. I'm always happy to help!

Best regards,
{AgentName}
{AgentPhone}
{AgentEmail}`
          }
        }
      },
      {
        key: 'welcome_day_30',
        name: 'Day 30 Follow-up',
        explanation: 'Checks how their plan is working after first month',
        channels: ['sms'],
        defaultEnabled: true,
        defaultTemplates: {
          sms: {
            content: `Hi {ClientFirstName}! It's {AgentName}. You've had your new {CarrierName} plan for about a month now - how's everything going? Are your doctors and prescriptions working smoothly with your coverage? Let me know if you need anything! {AgentPhone}`
          }
        }
      },
      {
        key: 'welcome_day_90',
        name: 'Day 90 Review',
        explanation: 'Offers a 90-day plan review to ensure satisfaction',
        channels: ['sms', 'email'],
        defaultEnabled: false,
        defaultTemplates: {
          sms: {
            content: `Hi {ClientFirstName}, it's {AgentName}! You've been on your {CarrierName} plan for 90 days now. Any questions? Everything going smoothly?`
          },
          email: {
            subject: '90-Day Plan Review - How Is Everything Going?',
            content: `Hi {ClientFirstName},

Can you believe it's already been 90 days since you enrolled in your new {CarrierName} plan? Time flies!

I like to check in with my clients at this milestone to make sure:
• Your plan is meeting your healthcare needs
• You're comfortable using your benefits
• Your doctors and prescriptions are working smoothly
• You don't have any unanswered questions

Looking forward to hearing from you!
{AgentName}
{AgentPhone}`
          }
        }
      }
    ]
  },
  {
    key: 'personal',
    name: 'Personal Touch',
    icon: Gift,
    description: 'Birthday and anniversary greetings to build relationships',
    color: 'pink',
    triggers: [
      {
        key: 'birthday',
        name: 'Birthday Greeting',
        explanation: 'Sends happy birthday wishes on their birthday',
        channels: ['sms', 'email'],
        defaultEnabled: true,
        defaultTemplates: {
          sms: {
            content: `Happy Birthday, {ClientFirstName}! 🎂 Wishing you a wonderful day filled with joy. It's a privilege to be your insurance agent. If you ever need anything, I'm just a call away! - {AgentName}`
          },
          email: {
            subject: 'Happy Birthday, {ClientFirstName}! 🎉',
            content: `Dear {ClientFirstName},

Happy Birthday! I hope your special day is filled with happiness, good health, and time spent with loved ones.

It's truly a privilege to be your insurance agent, and I wanted to take a moment to let you know how much I appreciate your trust in me.

Here's to a wonderful year ahead!

Warm wishes,
{AgentName}
{AgentPhone}`
          }
        }
      },
      {
        key: 'policy_anniversary',
        name: 'Policy Anniversary',
        explanation: 'Celebrates their Medicare enrollment anniversary',
        channels: ['sms', 'email'],
        defaultEnabled: true,
        defaultTemplates: {
          sms: {
            content: `Hi {ClientFirstName}! Today marks your {CarrierName} plan anniversary! Thank you for trusting me as your agent this past year. If your healthcare needs have changed, let's chat and make sure your plan still fits. - {AgentName} {AgentPhone}`
          },
          email: {
            subject: 'Happy Plan Anniversary, {ClientFirstName}!',
            content: `Hi {ClientFirstName},

I wanted to reach out and recognize a special milestone - it's been a year since you enrolled in your {CarrierName} plan!

Thank you for trusting me to help with your healthcare coverage. Being your agent is a responsibility I don't take lightly.

As we approach another year, this is a great time to:
• Review any changes in your health needs
• Check if your current plan still makes sense
• Discuss any concerns or questions you might have

If you'd like to schedule a quick review, I'm happy to make time for you.

Here's to another healthy year!
{AgentName}
{AgentPhone}
{AgentEmail}`
          }
        }
      }
    ]
  },
  {
    key: 'aep',
    name: 'AEP (Oct 15 - Dec 7)',
    icon: Calendar,
    description: 'Annual Enrollment Period reminders and outreach',
    color: 'purple',
    triggers: [
      {
        key: 'anoc_annual_review',
        name: 'ANOC & Annual Review',
        explanation: 'Combined ANOC letter notice + annual review reminder (September)',
        channels: ['sms', 'email'],
        defaultEnabled: true,
        defaultTemplates: {
          sms: {
            content: `Hi {ClientFirstName}, it's {AgentName}. You should be receiving your Annual Notice of Change (ANOC) letter from {CarrierName} soon. This shows any changes to your plan for next year. It's also time for your annual review - let's make sure your coverage still fits your needs! {AgentPhone}`
          },
          email: {
            subject: 'ANOC Letter Coming + Time for Your Annual Review',
            content: `Hi {ClientFirstName},

It's that time of year! {CarrierName} will be sending (or may have already sent) your Annual Notice of Change (ANOC) letter. This important document outlines any changes to your plan for next year.

Here's what to look for:
• Changes to monthly premiums
• Changes to drug coverage or costs
• Changes to your doctor network
• Any new benefits being added or removed

This is also a perfect time for your annual plan review. Even if you're happy with your current coverage, a lot can change in a year. I'd love to make sure your plan still meets your needs.

Would you like to schedule a quick review call?

Book a time: {AppointmentLink}
Or call/text me: {AgentPhone}

{AgentName}
{AgencyName}`
          }
        }
      },
      {
        key: 'aep_midpoint',
        name: 'AEP Midpoint Check',
        explanation: 'Mid-enrollment reminder (November 15)',
        channels: ['sms'],
        defaultEnabled: false,
        defaultTemplates: {
          sms: {
            content: `Hi {ClientFirstName}, it's {AgentName}. We're halfway through the enrollment period - only 3 weeks left! Have you had a chance to review your {CarrierName} plan options for next year? I'm here to help if you need me. {AgentPhone}`
          }
        }
      },
      {
        key: 'aep_final_week',
        name: 'AEP Final Week',
        explanation: 'Last chance reminder before deadline (December 1-7)',
        channels: ['sms', 'email'],
        defaultEnabled: true,
        defaultTemplates: {
          sms: {
            content: `Hi {ClientFirstName}! ⚠️ Annual enrollment ends Dec 7th - that's just days away! If you haven't reviewed your {CarrierName} plan options yet, there's still time. Want me to do a quick comparison? Call or text me ASAP: {AgentPhone} - {AgentName}`
          },
          email: {
            subject: '⏰ FINAL WEEK: Annual Enrollment Ends December 7th',
            content: `Hi {ClientFirstName},

This is your final reminder - the Annual Enrollment Period ends on December 7th!

If you haven't reviewed your {CarrierName} plan options yet, now is the time. After December 7th, you'll be locked into your current plan until next fall.

I can still help! Even with just a few days left, I can:
✓ Review your current coverage
✓ Compare plans available in your area
✓ Help you make a switch if needed
✓ Answer any last-minute questions

Don't miss this deadline. Give me a call or text today:
📞 {AgentPhone}

I'm available {OfficeHours} and can often accommodate urgent requests.

{AgentName}
{AgencyName}`
          }
        }
      }
    ]
  },
  {
    key: 'oep',
    name: 'OEP (Jan 1 - Mar 31)',
    icon: RefreshCw,
    description: 'Open Enrollment Period check-ins for plan switchers',
    color: 'amber',
    triggers: [
      {
        key: 'oep_jan2',
        name: 'January Check-in',
        explanation: 'New year greeting + plan working check (January 2)',
        channels: ['sms', 'email'],
        defaultEnabled: true,
        defaultTemplates: {
          sms: {
            content: `Happy New Year, {ClientFirstName}! It's {AgentName}. I hope 2026 is off to a great start! I wanted to reach out and make sure you have received your {CarrierName} cards. If you're experiencing any issues - like problems with your doctors, pharmacy, or coverage - now is a good time to ask any questions or address any concerns you may have. I'm here to help if you need me!`
          },
          email: {
            subject: 'Happy New Year! Quick Check-in on Your {CarrierName} Plan',
            content: `Hi {ClientFirstName},

Happy New Year! I hope 2026 is treating you well so far.

I wanted to reach out and make sure you have received your {CarrierName} cards. If you're experiencing any issues - like problems with your doctors, pharmacy, or coverage - now is a good time to ask any questions or address any concerns you may have.

I'm here to help if you need me!

{AgentName}
{AgentPhone}
{AgentEmail}`
          }
        }
      },
      {
        key: 'oep_feb1',
        name: 'February Follow-up',
        explanation: 'Mid-OEP check on plan satisfaction (February 1)',
        channels: ['sms', 'email'],
        defaultEnabled: true,
        defaultTemplates: {
          sms: {
            content: `Hi {ClientFirstName}, it's {AgentName}. We're about halfway through the Open Enrollment Period. How are things going with your {CarrierName} plan? If you've had any issues or concerns, there's still time to make a change before March 31st. Text or call me: {AgentPhone}`
          },
          email: {
            subject: 'February Check-in: How Is Your {CarrierName} Plan Working?',
            content: `Hi {ClientFirstName},

I hope February is treating you well! I wanted to check in as we're now halfway through the Open Enrollment Period.

Have you had any issues with your {CarrierName} plan since January 1st? Some common concerns I hear from clients include:
• Doctors or specialists not being in-network
• Prescription costs being higher than expected
• Difficulty getting referrals or prior authorizations
• Coverage gaps for specific treatments

If any of this sounds familiar, you still have until March 31st to switch to a different plan that better fits your needs.

Even if everything is going smoothly, I'm always here if questions come up. Don't hesitate to reach out!

{AgentName}
{AgentPhone}
{AgentEmail}`
          }
        }
      },
      {
        key: 'oep_mar1',
        name: 'March Deadline Reminder',
        explanation: 'Final OEP reminder - deadline March 31 (March 1)',
        channels: ['sms', 'email'],
        defaultEnabled: true,
        defaultTemplates: {
          sms: {
            content: `Hi {ClientFirstName}! ⏰ {AgentName} here with a reminder: The Open Enrollment Period ends March 31st. If you've had any issues with your {CarrierName} plan this year, now is your last chance to switch. Need help? Call/text: {AgentPhone}`
          },
          email: {
            subject: '⏰ Final Month: Open Enrollment Ends March 31st',
            content: `Hi {ClientFirstName},

This is my final reminder about the Open Enrollment Period - it ends on March 31st.

If you've had ANY concerns about your current {CarrierName} plan, now is the time to act:
• Problems with doctor access?
• Prescription costs too high?
• Coverage not meeting your needs?
• Unhappy with the plan for any reason?

After March 31st, you'll generally need to wait until the fall Annual Enrollment Period to make changes (unless you qualify for a Special Enrollment Period).

If you want to explore your options, please reach out soon so we have time to review everything:
📞 {AgentPhone}
📅 Book online: {AppointmentLink}

I'm here to help!

{AgentName}
{AgencyName}`
          }
        }
      }
    ]
  },
  {
    key: 'health_alerts',
    name: 'Health Alerts',
    icon: Heart,
    description: 'Proactive alerts based on health events - creates tasks for agent investigation',
    color: 'red',
    triggers: [
      {
        key: 'new_rx_prescribed',
        name: 'New Prescription',
        explanation: 'Creates alert and investigation task when any new Rx is prescribed',
        channels: ['alert'],
        actionType: 'alert_and_task',
        taskType: 'investigate',
        defaultEnabled: true,
        investigationOutcomes: [
          { key: 'no_action', label: 'Investigation complete - no outreach needed' },
          { key: 'outreach_satisfied', label: 'Investigation complete - outreach completed, client satisfied' },
          { key: 'outreach_followup', label: 'Investigation complete - outreach completed, follow-up scheduled' },
          { key: 'outreach_issue', label: 'Investigation complete - outreach completed, issue identified' }
        ],
        defaultTemplates: {}
      },
      {
        key: 'brand_name_drug',
        name: 'Brand Name Drug Prescribed',
        explanation: 'Creates alert, investigation task, and optional outreach for brand name medications',
        channels: ['sms', 'email', 'alert'],
        actionType: 'alert_task_and_outreach',
        taskType: 'investigate_and_call',
        defaultEnabled: true,
        supportsLanguagePreference: true,
        investigationOutcomes: [
          { key: 'no_action', label: 'Investigation complete - no outreach needed' },
          { key: 'outreach_satisfied', label: 'Investigation complete - outreach completed, client satisfied' },
          { key: 'generic_available', label: 'Investigation complete - generic alternative discussed' },
          { key: 'cost_assistance', label: 'Investigation complete - cost assistance program identified' }
        ],
        defaultTemplates: {
          sms: {
            content: `Hi {ClientFirstName}, it's {AgentName}. I saw you were recently prescribed a brand name medication. Just want to make sure you don't have any questions about coverage, cost, options etc. Reach out to me if you do.`
          },
          email: {
            subject: 'Quick Chat About Your New Prescription',
            content: `Hi {ClientFirstName},

I noticed you were recently prescribed a brand name medication and wanted to reach out.

Brand name drugs can sometimes have higher costs, and I want to make sure you:
• Understand your {CarrierName} coverage for this medication
• Know about any generic alternatives that might be available
• Are aware of manufacturer savings programs or coupons
• Have the most cost-effective pharmacy option

Would you have a few minutes for a quick call? I'm happy to review your options with you.

{AgentName}
{AgentPhone}
{AgentEmail}`
          }
        },
        defaultTemplates_es: {
          sms: {
            content: `Hola {ClientFirstName}, soy {AgentName}. Vi que le recetaron recientemente un medicamento de marca. Me gustaría hablar con usted sobre sus opciones de cobertura y asegurarme de que esté obteniendo el mejor valor. ¿Tiene unos minutos para hablar? {AgentPhone}`
          },
          email: {
            subject: 'Conversación Rápida Sobre Su Nueva Receta',
            content: `Hola {ClientFirstName},

Noté que le recetaron recientemente un medicamento de marca y quería comunicarme con usted.

Los medicamentos de marca a veces pueden tener costos más altos, y quiero asegurarme de que:
• Entienda su cobertura de {CarrierName} para este medicamento
• Conozca las alternativas genéricas que podrían estar disponibles
• Esté al tanto de los programas de ahorro del fabricante o cupones
• Tenga la opción de farmacia más económica

¿Tendría unos minutos para una llamada rápida? Con gusto revisaré sus opciones con usted.

{AgentName}
{AgentPhone}
{AgentEmail}`
          }
        }
      },
      {
        key: 'specialty_drug_alert',
        name: 'Specialty Drug Prescribed',
        explanation: 'Creates alert, investigation task, and outreach for specialty medications',
        channels: ['sms', 'email', 'alert'],
        actionType: 'alert_task_and_outreach',
        taskType: 'investigate_and_call',
        defaultEnabled: true,
        supportsLanguagePreference: true,
        investigationOutcomes: [
          { key: 'no_action', label: 'Investigation complete - no outreach needed' },
          { key: 'outreach_satisfied', label: 'Investigation complete - outreach completed, client satisfied' },
          { key: 'specialty_pharmacy', label: 'Investigation complete - specialty pharmacy coordination done' },
          { key: 'prior_auth_needed', label: 'Investigation complete - prior auth assistance provided' },
          { key: 'cost_assistance', label: 'Investigation complete - patient assistance program identified' }
        ],
        defaultTemplates: {
          sms: {
            content: `Hi {ClientFirstName}, it's {AgentName}. I saw you were prescribed a specialty medication recently. These often have specific coverage requirements and I want to make sure you have all the support you need. Let me know if you would like to chat. {AgentPhone}`
          },
          email: {
            subject: 'Important: About Your Specialty Medication',
            content: `Hi {ClientFirstName},

I'm reaching out because I noticed you were recently prescribed a specialty medication. These medications often have unique requirements, and I want to make sure you're fully supported.

Here's how I can help:
• Explain your {CarrierName} coverage and any step therapy requirements
• Coordinate with specialty pharmacies if needed
• Help with prior authorization paperwork
• Identify manufacturer patient assistance programs
• Ensure you understand the cost-sharing for this medication

Specialty medications can be complex, but you don't have to navigate this alone. Please give me a call when you have a moment.

{AgentName}
{AgentPhone}
{AgentEmail}`
          }
        },
        defaultTemplates_es: {
          sms: {
            content: `Hola {ClientFirstName}, soy {AgentName}. Vi que le recetaron un medicamento especializado recientemente. Estos a menudo tienen requisitos específicos de cobertura y quiero asegurarme de que tenga todo el apoyo que necesita. ¿Podemos hablar? {AgentPhone}`
          },
          email: {
            subject: 'Importante: Sobre Su Medicamento Especializado',
            content: `Hola {ClientFirstName},

Me comunico porque noté que le recetaron recientemente un medicamento especializado. Estos medicamentos a menudo tienen requisitos únicos, y quiero asegurarme de que esté completamente apoyado.

Así es como puedo ayudar:
• Explicar su cobertura de {CarrierName} y cualquier requisito de terapia escalonada
• Coordinar con farmacias especializadas si es necesario
• Ayudar con el papeleo de autorización previa
• Identificar programas de asistencia al paciente del fabricante
• Asegurar que entienda el costo compartido de este medicamento

Los medicamentos especializados pueden ser complejos, pero no tiene que navegar esto solo. Por favor llámeme cuando tenga un momento.

{AgentName}
{AgentPhone}
{AgentEmail}`
          }
        }
      },
      {
        key: 'rx_denial_alert',
        name: 'Prescription Denial',
        explanation: 'Creates alert, task, and outreach when a prescription is denied',
        channels: ['sms', 'email', 'alert'],
        actionType: 'alert_task_and_outreach',
        taskType: 'investigate_and_call',
        defaultEnabled: true,
        supportsLanguagePreference: true,
        investigationOutcomes: [
          { key: 'no_action', label: 'Investigation complete - client resolved independently' },
          { key: 'appeal_submitted', label: 'Investigation complete - appeal submitted' },
          { key: 'alternative_found', label: 'Investigation complete - alternative medication found' },
          { key: 'prior_auth_submitted', label: 'Investigation complete - prior auth submitted' },
          { key: 'outreach_satisfied', label: 'Investigation complete - outreach completed, client satisfied' }
        ],
        defaultTemplates: {
          sms: {
            content: `Hi {ClientFirstName}, it's {AgentName}. I see you may have had a prescription denied by {CarrierName}. That can be frustrating - I'm here to help! Let's talk about your options. Call or text me: {AgentPhone}`
          },
          email: {
            subject: 'Let Me Help With Your Prescription Denial',
            content: `Hi {ClientFirstName},

I'm reaching out because I understand you may have experienced a prescription denial recently. I know how frustrating this can be, especially when you need your medication.

Here's how I can help:
• Explain why the denial occurred
• Guide you through the appeals process
• Help gather supporting documentation from your doctor
• Explore alternative medications that may be covered
• Check for patient assistance programs

Please don't wait on this - I'm here to help you get the medication you need. Give me a call today.

{AgentName}
{AgentPhone}
{AgentEmail}`
          }
        },
        defaultTemplates_es: {
          sms: {
            content: `Hola {ClientFirstName}, soy {AgentName}. Veo que {CarrierName} puede haber denegado una receta. Eso puede ser frustrante - ¡estoy aquí para ayudar! Hablemos de sus opciones. Llámeme o envíeme un mensaje: {AgentPhone}`
          },
          email: {
            subject: 'Permítame Ayudarle Con Su Receta Denegada',
            content: `Hola {ClientFirstName},

Me comunico porque entiendo que puede haber experimentado una denegación de receta recientemente. Sé lo frustrante que puede ser esto, especialmente cuando necesita su medicamento.

Así es como puedo ayudar:
• Explicar por qué ocurrió la denegación
• Guiarle a través del proceso de apelación
• Ayudar a reunir documentación de apoyo de su médico
• Explorar medicamentos alternativos que puedan estar cubiertos
• Buscar programas de asistencia al paciente

Por favor no espere - estoy aquí para ayudarle a obtener el medicamento que necesita. Llámeme hoy.

{AgentName}
{AgentPhone}
{AgentEmail}`
          }
        }
      },
      {
        key: 'prior_auth_required',
        name: 'Prior Authorization Required',
        explanation: 'Creates alert and task when prior authorization is needed for a medication or service',
        channels: ['sms', 'email', 'alert'],
        actionType: 'alert_task_and_outreach',
        taskType: 'investigate_and_assist',
        defaultEnabled: true,
        supportsLanguagePreference: true,
        investigationOutcomes: [
          { key: 'auth_approved', label: 'Investigation complete - prior auth approved' },
          { key: 'auth_pending', label: 'Investigation complete - prior auth submitted, pending' },
          { key: 'auth_denied', label: 'Investigation complete - prior auth denied, appeal in progress' },
          { key: 'alternative_found', label: 'Investigation complete - alternative not requiring PA found' },
          { key: 'outreach_satisfied', label: 'Investigation complete - client assisted, satisfied' }
        ],
        defaultTemplates: {
          sms: {
            content: `Hi {ClientFirstName}, it's {AgentName}. I noticed {CarrierName} requires a prior authorization for a recent prescription or service. I can help you navigate this process. Let me know if you need a quick chat! {AgentPhone}`
          },
          email: {
            subject: 'Prior Authorization Needed - I Can Help',
            content: `Hi {ClientFirstName},

I noticed that {CarrierName} requires a prior authorization for a recent prescription or service you need. I know this process can be confusing, but I'm here to help.

What is Prior Authorization?
Prior authorization is when your insurance company requires approval before covering certain medications or services. This is common for specialty drugs, certain procedures, and some medical equipment.

How I Can Help:
• Explain what information is needed
• Coordinate with your doctor's office
• Track the status of your authorization
• Help with appeals if the initial request is denied

Don't let paperwork stand in the way of your care. Please reach out and I'll guide you through this.

{AgentName}
{AgentPhone}
{AgentEmail}`
          }
        },
        defaultTemplates_es: {
          sms: {
            content: `Hola {ClientFirstName}, soy {AgentName}. Noté que {CarrierName} requiere una autorización previa para una receta o servicio reciente. Puedo ayudarle a navegar este proceso. Conectémonos: {AgentPhone}`
          },
          email: {
            subject: 'Se Necesita Autorización Previa - Puedo Ayudar',
            content: `Hola {ClientFirstName},

Noté que {CarrierName} requiere una autorización previa para una receta o servicio reciente que necesita. Sé que este proceso puede ser confuso, pero estoy aquí para ayudar.

¿Qué es la Autorización Previa?
La autorización previa es cuando su compañía de seguros requiere aprobación antes de cubrir ciertos medicamentos o servicios. Esto es común para medicamentos especializados, ciertos procedimientos y algunos equipos médicos.

Cómo Puedo Ayudar:
• Explicar qué información se necesita
• Coordinar con el consultorio de su médico
• Rastrear el estado de su autorización
• Ayudar con apelaciones si la solicitud inicial es denegada

No deje que el papeleo se interponga en el camino de su atención. Por favor comuníquese y le guiaré a través de esto.

{AgentName}
{AgentPhone}
{AgentEmail}`
          }
        }
      },
      {
        key: 'er_visit_followup',
        name: 'ER Visit Follow-up',
        explanation: 'Creates alert and sends check-in message 3 days after emergency room visit',
        channels: ['sms', 'alert'],
        actionType: 'alert_and_outreach',
        defaultEnabled: true,
        supportsLanguagePreference: true,
        defaultTemplates: {
          sms: {
            content: `Hi {ClientFirstName}, it's {AgentName}. I heard you had a recent ER visit and wanted to check in. I hope you're feeling better! If you have any questions about your {CarrierName} coverage or any bills you received, I'm here to help. Take care of yourself! {AgentPhone}`
          }
        },
        defaultTemplates_es: {
          sms: {
            content: `Hola {ClientFirstName}, soy {AgentName}. Supe que tuvo una visita reciente a la sala de emergencias y quería verificar cómo está. ¡Espero que se sienta mejor! Si tiene alguna pregunta sobre su cobertura de {CarrierName} o facturas próximas, estoy aquí para ayudar. ¡Cuídese! {AgentPhone}`
          }
        }
      },
      {
        key: 'risk_score_increase',
        name: 'Risk Score Threshold Increase',
        explanation: 'Creates alert and investigation task when client risk score rises to a higher threshold',
        channels: ['alert'],
        actionType: 'alert_and_task',
        taskType: 'investigate',
        defaultEnabled: true,
        investigationOutcomes: [
          { key: 'no_action', label: 'Investigation complete - score increase explained, no action needed' },
          { key: 'outreach_completed', label: 'Investigation complete - proactive outreach completed' },
          { key: 'plan_review_scheduled', label: 'Investigation complete - plan review scheduled' },
          { key: 'care_coordination', label: 'Investigation complete - care coordination initiated' },
          { key: 'high_priority', label: 'Investigation complete - flagged for high-priority monitoring' }
        ],
        defaultTemplates: {}
      }
    ]
  },
  {
    key: 'reengagement',
    name: 'Re-engagement',
    icon: MessageCircle,
    description: 'Win back inactive or at-risk clients',
    color: 'green',
    triggers: [
      {
        key: 'inactive_100_days',
        name: '100 Day Check-in',
        explanation: 'Reaches out after 100 days of no contact',
        channels: ['sms'],
        defaultEnabled: true,
        defaultTemplates: {
          sms: {
            content: `Hi {ClientFirstName}! It's {AgentName}, your insurance agent. It's been a little while since we connected and I wanted to check in. How are things going with your {CarrierName} plan? If you have any questions or need anything, I'm always here for you! {AgentPhone}`
          }
        }
      },
      {
        key: 'high_risk_outreach',
        name: 'High Risk Client',
        explanation: 'Creates task for priority contact with clients with high churn risk (no SMS - alert/task only)',
        channels: ['alert'],
        actionType: 'alert_and_task',
        taskType: 'investigate_and_call',
        defaultEnabled: true,
        investigationOutcomes: [
          { key: 'no_action', label: 'Investigation complete - client stable, no action needed' },
          { key: 'outreach_completed', label: 'Investigation complete - proactive outreach completed' },
          { key: 'plan_review_scheduled', label: 'Investigation complete - plan review scheduled' },
          { key: 'issue_resolved', label: 'Investigation complete - concern addressed, client satisfied' },
          { key: 'at_risk', label: 'Investigation complete - still at risk, monitoring' }
        ],
        defaultTemplates: {}
      }
    ]
  },
];

// Color mapping for categories
export const CATEGORY_COLORS = {
  blue: {
    bg: 'bg-blue-500/20',
    border: 'border-blue-500/30',
    text: 'text-blue-400',
    badge: 'bg-blue-500/30 text-blue-300',
    icon: 'text-blue-400'
  },
  pink: {
    bg: 'bg-pink-500/20',
    border: 'border-pink-500/30',
    text: 'text-pink-400',
    badge: 'bg-pink-500/30 text-pink-300',
    icon: 'text-pink-400'
  },
  purple: {
    bg: 'bg-purple-500/20',
    border: 'border-purple-500/30',
    text: 'text-purple-400',
    badge: 'bg-purple-500/30 text-purple-300',
    icon: 'text-purple-400'
  },
  amber: {
    bg: 'bg-amber-500/20',
    border: 'border-amber-500/30',
    text: 'text-amber-400',
    badge: 'bg-amber-500/30 text-amber-300',
    icon: 'text-amber-400'
  },
  red: {
    bg: 'bg-red-500/20',
    border: 'border-red-500/30',
    text: 'text-red-400',
    badge: 'bg-red-500/30 text-red-300',
    icon: 'text-red-400'
  },
  green: {
    bg: 'bg-green-500/20',
    border: 'border-green-500/30',
    text: 'text-green-400',
    badge: 'bg-green-500/30 text-green-300',
    icon: 'text-green-400'
  },
  indigo: {
    bg: 'bg-indigo-500/20',
    border: 'border-indigo-500/30',
    text: 'text-indigo-400',
    badge: 'bg-indigo-500/30 text-indigo-300',
    icon: 'text-indigo-400'
  }
};

// Channel icons
export const CHANNEL_ICONS = {
  sms: MessageSquare,
  email: Mail
};

// Get all triggers as a flat list
export const getAllTriggers = () => {
  return AUTOMATION_CATEGORIES.flatMap(cat =>
    cat.triggers.map(trigger => ({
      ...trigger,
      category: cat.key,
      categoryName: cat.name
    }))
  );
};

// Get trigger by key
export const getTriggerByKey = (key) => {
  for (const category of AUTOMATION_CATEGORIES) {
    const trigger = category.triggers.find(t => t.key === key);
    if (trigger) {
      return { ...trigger, category: category.key, categoryName: category.name };
    }
  }
  return null;
};

// Get default template for a trigger
export const getDefaultTemplate = (triggerKey, channel) => {
  const trigger = getTriggerByKey(triggerKey);
  if (!trigger || !trigger.defaultTemplates) return null;
  return trigger.defaultTemplates[channel] || null;
};

// Get default template for a trigger with language preference support
export const getDefaultTemplateByLanguage = (triggerKey, channel, language = 'en') => {
  const trigger = getTriggerByKey(triggerKey);
  if (!trigger) return null;

  // If Spanish is requested and Spanish templates exist, use them
  if (language === 'es' && trigger.defaultTemplates_es && trigger.defaultTemplates_es[channel]) {
    return trigger.defaultTemplates_es[channel];
  }

  // Fall back to English templates
  if (trigger.defaultTemplates && trigger.defaultTemplates[channel]) {
    return trigger.defaultTemplates[channel];
  }

  return null;
};

// Check if a trigger supports language preference
export const triggerSupportsLanguage = (triggerKey) => {
  const trigger = getTriggerByKey(triggerKey);
  return trigger?.supportsLanguagePreference || false;
};

// Get investigation outcomes for a trigger
export const getInvestigationOutcomes = (triggerKey) => {
  const trigger = getTriggerByKey(triggerKey);
  return trigger?.investigationOutcomes || [];
};

// Get action type for a trigger (alert_only, alert_and_task, alert_and_outreach, alert_task_and_outreach)
export const getTriggerActionType = (triggerKey) => {
  const trigger = getTriggerByKey(triggerKey);
  return trigger?.actionType || 'alert_only';
};

// Get task type for a trigger (investigate, investigate_and_call, investigate_and_assist, investigate_and_followup)
export const getTriggerTaskType = (triggerKey) => {
  const trigger = getTriggerByKey(triggerKey);
  return trigger?.taskType || null;
};
